/**
 * Clinical Logic Layer Dashboard
 * 
 * Provides:
 * - 14-Day Lookback visualization
 * - POC Change proposals with "Proposed - Awaiting Review" status
 * - Frequency alerts with PPS decline tracking
 * - IDG discussion items
 * - Medicare CoPs compliance status
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Progress } from "./ui/progress";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Heart,
  TrendingDown,
  TrendingUp,
  Minus,
  RefreshCw,
  Users,
  Shield,
  ClipboardList,
  Calendar,
  ArrowUp,
  ArrowDown,
  XCircle,
  Check,
  X,
  Play,
  Bell,
  Stethoscope,
  Brain,
  MessageSquare,
  Eye,
  ChevronRight,
  AlertCircle,
  Zap
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

// Trend indicator component
function TrendIndicator({ trend, label }) {
  const config = {
    improving: { icon: TrendingDown, color: "text-green-400", bg: "bg-green-500/10" },
    worsening: { icon: TrendingUp, color: "text-red-400", bg: "bg-red-500/10" },
    stable: { icon: Minus, color: "text-slate-400", bg: "bg-slate-500/10" },
    declining: { icon: TrendingDown, color: "text-amber-400", bg: "bg-amber-500/10" },
    unknown: { icon: Minus, color: "text-slate-500", bg: "bg-slate-500/10" }
  };
  
  const { icon: Icon, color, bg } = config[trend] || config.unknown;
  
  return (
    <div className={`flex items-center gap-2 px-2 py-1 rounded ${bg}`}>
      <Icon className={`w-4 h-4 ${color}`} />
      <span className={`text-sm ${color}`}>{label || trend}</span>
    </div>
  );
}

// PPS Phase Badge
function PPSPhaseBadge({ phase, score }) {
  const config = {
    stable: { color: "bg-green-500/20 text-green-300", label: "Stable" },
    transitional: { color: "bg-amber-500/20 text-amber-300", label: "Transitional" },
    end_stage: { color: "bg-orange-500/20 text-orange-300", label: "End Stage" },
    active_dying: { color: "bg-red-500/20 text-red-300", label: "Active Dying" },
    unknown: { color: "bg-slate-500/20 text-slate-300", label: "Unknown" }
  };
  
  const { color, label } = config[phase] || config.unknown;
  
  return (
    <Badge className={color}>
      PPS {score}% - {label}
    </Badge>
  );
}

// Proposed Change Card
function ProposedChangeCard({ change, onReview }) {
  const categoryIcons = {
    pain: Activity,
    symptoms: Heart,
    functional: TrendingDown,
    psychosocial: Users,
    spiritual: Brain,
    comfort: Stethoscope
  };
  
  const Icon = categoryIcons[change.category] || FileText;
  
  return (
    <Card className="bg-slate-800/50 border-slate-700 border-l-4 border-l-amber-500">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Icon className="w-4 h-4 text-amber-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="capitalize text-xs">
                  {change.change_type}
                </Badge>
                <Badge className="bg-amber-500/20 text-amber-300 text-xs">
                  Proposed - Awaiting Review
                </Badge>
              </div>
              <p className="text-sm font-medium">{change.proposed_value}</p>
              <p className="text-xs text-slate-400 mt-1">{change.rationale}</p>
              <p className="text-xs text-slate-500 mt-2 italic">Source: {change.source_notes}</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 mt-4">
          <Button 
            size="sm" 
            className="bg-green-600 hover:bg-green-700"
            onClick={() => onReview(change.id, "approve")}
          >
            <Check className="w-3 h-3 mr-1" />
            Approve
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            className="text-red-400 border-red-500/30 hover:bg-red-500/10"
            onClick={() => onReview(change.id, "reject")}
          >
            <X className="w-3 h-3 mr-1" />
            Reject
          </Button>
          <Badge variant="outline" className="ml-auto text-xs">
            <Calendar className="w-3 h-3 mr-1" />
            Flagged for IDG
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}

// Frequency Alert Card
function FrequencyAlertCard({ alert, onAcknowledge }) {
  const severityConfig = {
    info: { color: "border-l-blue-500", bg: "bg-blue-500/10", icon: AlertCircle },
    warning: { color: "border-l-amber-500", bg: "bg-amber-500/10", icon: AlertTriangle },
    urgent: { color: "border-l-orange-500", bg: "bg-orange-500/10", icon: AlertTriangle },
    critical: { color: "border-l-red-500", bg: "bg-red-500/10", icon: XCircle }
  };
  
  const config = severityConfig[alert.severity] || severityConfig.warning;
  const Icon = config.icon;
  
  return (
    <Card className={`bg-slate-800/50 border-slate-700 border-l-4 ${config.color}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${config.bg}`}>
            <Icon className="w-5 h-5 text-current" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={`uppercase text-xs ${
                alert.severity === "critical" ? "bg-red-500/20 text-red-300" :
                alert.severity === "urgent" ? "bg-orange-500/20 text-orange-300" :
                "bg-amber-500/20 text-amber-300"
              }`}>
                {alert.severity}
              </Badge>
              {alert.imminent_death_flagged && (
                <Badge className="bg-red-500/20 text-red-300 text-xs">
                  Imminent Death Flagged
                </Badge>
              )}
            </div>
            
            <p className="font-medium text-sm">{alert.rationale}</p>
            
            {alert.pps_decline && (
              <div className="mt-2 p-2 bg-slate-900 rounded text-xs">
                <span className="text-slate-400">PPS Decline:</span>
                <span className="text-red-400 ml-2">{alert.pps_previous}% → {alert.pps_current}%</span>
                <span className="text-slate-500 ml-2">({alert.pps_decline}% decline)</span>
              </div>
            )}
            
            <div className="mt-3 space-y-1">
              <p className="text-xs text-slate-400">Recommended Frequencies:</p>
              <p className="text-sm">
                <span className="text-slate-500">Nursing:</span>
                <span className="text-teal-400 ml-2">{alert.recommended_nursing_frequency}</span>
              </p>
              {alert.recommended_sw_frequency && (
                <p className="text-sm">
                  <span className="text-slate-500">Social Work:</span>
                  <span className="text-teal-400 ml-2">{alert.recommended_sw_frequency}</span>
                </p>
              )}
              {alert.recommended_chaplain_frequency && (
                <p className="text-sm">
                  <span className="text-slate-500">Chaplain:</span>
                  <span className="text-teal-400 ml-2">{alert.recommended_chaplain_frequency}</span>
                </p>
              )}
            </div>
            
            <p className="text-xs text-slate-500 mt-2 italic">
              Medicare CoP Reference: {alert.medicare_cop_reference}
            </p>
          </div>
        </div>
        
        <div className="mt-4">
          <Button 
            size="sm" 
            onClick={() => onAcknowledge(alert.id)}
            className="w-full"
          >
            <Check className="w-3 h-3 mr-1" />
            Acknowledge & Take Action
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Compliance Status Card
function ComplianceStatusCard({ compliance }) {
  const isCompliant = compliance.compliance_status === "compliant";
  
  return (
    <Card className={`border-l-4 ${isCompliant ? "border-l-green-500" : "border-l-red-500"}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Shield className={`w-4 h-4 ${isCompliant ? "text-green-400" : "text-red-400"}`} />
          Medicare CoPs Compliance
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-3">
          <Badge className={isCompliant ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}>
            {isCompliant ? "Compliant" : `${compliance.issue_count} Issues`}
          </Badge>
          <span className="text-xs text-slate-400">
            {compliance.visit_counts?.total || 0} visits in 14 days
          </span>
        </div>
        
        {!isCompliant && compliance.issues?.length > 0 && (
          <div className="space-y-2">
            {compliance.issues.map((issue, idx) => (
              <div key={idx} className="text-xs p-2 bg-red-500/10 rounded border border-red-500/20">
                <AlertTriangle className="w-3 h-3 inline mr-1 text-red-400" />
                {issue}
              </div>
            ))}
          </div>
        )}
        
        <Separator className="my-3" />
        
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold text-teal-400">{compliance.visit_counts?.nursing || 0}</p>
            <p className="text-xs text-slate-400">Nursing</p>
          </div>
          <div>
            <p className="text-lg font-bold text-purple-400">{compliance.visit_counts?.social_work || 0}</p>
            <p className="text-xs text-slate-400">Social Work</p>
          </div>
          <div>
            <p className="text-lg font-bold text-amber-400">{compliance.visit_counts?.chaplain || 0}</p>
            <p className="text-xs text-slate-400">Chaplain</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Clinical Logic Dashboard Component
export function ClinicalLogicDashboard({ patientId, patientName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [synthesizing, setSynthesizing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [synthesis, setSynthesis] = useState(null);
  const [proposedChanges, setProposedChanges] = useState([]);
  const [frequencyAlerts, setFrequencyAlerts] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [reviewDialog, setReviewDialog] = useState({ open: false, changeId: null, action: null });
  const [reviewNotes, setReviewNotes] = useState("");
  const [activeDyingShown, setActiveDyingShown] = useState(false);

  // Check for Active Dying phase and show alerts
  useEffect(() => {
    if (dashboard?.clinical_status?.pps_phase === "active_dying" && !activeDyingShown) {
      setActiveDyingShown(true);
      toast.error("ACTIVE DYING PHASE DETECTED", {
        description: `${patientName} has entered active dying phase. Consider continuous care or GIP level.`,
        duration: 10000,
        important: true,
      });
    }
    // Also check frequency alerts for imminent death flag
    const imminentAlert = frequencyAlerts.find(a => a.imminent_death_flagged);
    if (imminentAlert && !activeDyingShown) {
      setActiveDyingShown(true);
      toast.error("IMMINENT DEATH FLAGGED", {
        description: `Clinical indicators suggest ${patientName} may be approaching end of life. Immediate review recommended.`,
        duration: 10000,
        important: true,
      });
    }
  }, [dashboard, frequencyAlerts, patientName, activeDyingShown]);

  useEffect(() => {
    fetchData();
  }, [patientId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, changesRes, alertsRes, complianceRes] = await Promise.all([
        axios.get(`${API_URL}/api/clinical-logic/dashboard/${patientId}`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/clinical-logic/proposed-changes/${patientId}`).catch(() => ({ data: { changes: [] } })),
        axios.get(`${API_URL}/api/clinical-logic/frequency-alerts/${patientId}?acknowledged=false`).catch(() => ({ data: { alerts: [] } })),
        axios.get(`${API_URL}/api/clinical-logic/compliance/${patientId}`).catch(() => ({ data: null }))
      ]);
      
      setDashboard(dashboardRes.data);
      setProposedChanges(changesRes.data.changes || []);
      setFrequencyAlerts(alertsRes.data.alerts || []);
      setCompliance(complianceRes.data);
      
      // Try to get latest synthesis
      try {
        const synthesisRes = await axios.get(`${API_URL}/api/clinical-logic/synthesis/${patientId}`);
        setSynthesis(synthesisRes.data);
      } catch (e) {
        // No synthesis yet
      }
    } catch (err) {
      console.error("Failed to fetch clinical logic data:", err);
    } finally {
      setLoading(false);
    }
  };

  const runSynthesis = async () => {
    setSynthesizing(true);
    try {
      const response = await axios.post(`${API_URL}/api/clinical-logic/synthesize/${patientId}`);
      
      if (response.data.success) {
        toast.success("Clinical synthesis complete", {
          description: `${response.data.proposed_changes_count} POC changes proposed, ${response.data.alerts_count} alerts generated`
        });
        fetchData();
      }
    } catch (err) {
      toast.error("Failed to run clinical synthesis");
    } finally {
      setSynthesizing(false);
    }
  };

  const handleReviewChange = (changeId, action) => {
    setReviewDialog({ open: true, changeId, action });
  };

  const submitReview = async () => {
    try {
      await axios.put(`${API_URL}/api/clinical-logic/proposed-changes/${reviewDialog.changeId}/review`, null, {
        params: {
          action: reviewDialog.action,
          reviewer_id: "current-user",
          reviewer_name: "Clinical Staff",
          notes: reviewNotes
        }
      });
      
      toast.success(`Change ${reviewDialog.action === "approve" ? "approved" : "rejected"}`);
      setReviewDialog({ open: false, changeId: null, action: null });
      setReviewNotes("");
      fetchData();
    } catch (err) {
      toast.error("Failed to submit review");
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await axios.put(`${API_URL}/api/clinical-logic/frequency-alerts/${alertId}/acknowledge`, null, {
        params: {
          acknowledger_id: "current-user",
          acknowledger_name: "Clinical Staff"
        }
      });
      
      toast.success("Alert acknowledged");
      fetchData();
    } catch (err) {
      toast.error("Failed to acknowledge alert");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-teal-400" />
        <span className="ml-2 text-slate-400">Loading clinical data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-teal-500/20 to-cyan-500/20">
            <Brain className="w-6 h-6 text-teal-400" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Clinical Logic Layer</h2>
            <p className="text-xs text-slate-400">
              14-Day Lookback Analysis • {patientName}
            </p>
          </div>
        </div>
        <Button onClick={runSynthesis} disabled={synthesizing}>
          {synthesizing ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
          ) : (
            <><Play className="w-4 h-4 mr-2" /> Run Synthesis</>
          )}
        </Button>
      </div>

      {/* ACTIVE PHASE BANNER - Critical Alert for Active Dying */}
      {(dashboard?.clinical_status?.pps_phase === "active_dying" || 
        frequencyAlerts.some(a => a.imminent_death_flagged)) && (
        <Alert className="bg-gradient-to-r from-red-900/80 to-red-800/60 border-red-500 border-2 animate-pulse">
          <AlertTriangle className="w-5 h-5 text-red-300" />
          <AlertTitle className="text-red-100 font-bold text-lg flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-red-500 animate-ping" />
            ACTIVE DYING PHASE
          </AlertTitle>
          <AlertDescription className="text-red-200 mt-2">
            <div className="space-y-2">
              <p className="font-medium">
                {patientName} has entered the active phase of dying. Immediate clinical review required.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Badge className="bg-red-600 text-white">PPS: {dashboard?.clinical_status?.pps_current || "≤20"}%</Badge>
                <Badge className="bg-orange-600 text-white">Consider: Continuous Care or GIP</Badge>
                <Badge className="bg-amber-600 text-white">Notify: MD, Family, IDG</Badge>
              </div>
              <p className="text-xs text-red-300 mt-2 italic">
                Medicare CoP 418.56: Document all level-of-care changes and ensure 24/7 nursing availability consideration.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Quick Stats */}
      {dashboard && (
        <div className="grid grid-cols-4 gap-3">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-teal-400">{dashboard.visit_summary?.total_14_day || 0}</p>
              <p className="text-xs text-slate-400">Visits (14 days)</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-400">{proposedChanges.length}</p>
              <p className="text-xs text-slate-400">Pending Reviews</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-red-400">{frequencyAlerts.length}</p>
              <p className="text-xs text-slate-400">Active Alerts</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-purple-400">{dashboard.idg_items?.length || 0}</p>
              <p className="text-xs text-slate-400">IDG Items</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Clinical Status Overview */}
      {dashboard && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Clinical Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400">Pain:</span>
                <TrendIndicator trend={dashboard.clinical_status?.pain_trend} />
              </div>
              {dashboard.clinical_status?.pps_current && (
                <PPSPhaseBadge 
                  phase={dashboard.clinical_status.pps_phase} 
                  score={dashboard.clinical_status.pps_current} 
                />
              )}
              {dashboard.clinical_status?.compliance_issues > 0 && (
                <Badge className="bg-red-500/20 text-red-300">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {dashboard.clinical_status.compliance_issues} Compliance Issues
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full bg-slate-800">
          <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
          <TabsTrigger value="changes" className="text-xs">
            POC Changes
            {proposedChanges.length > 0 && (
              <Badge className="ml-1 bg-amber-500/20 text-amber-300 text-xs">{proposedChanges.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="alerts" className="text-xs">
            Alerts
            {frequencyAlerts.length > 0 && (
              <Badge className="ml-1 bg-red-500/20 text-red-300 text-xs">{frequencyAlerts.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="idg" className="text-xs">IDG Items</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Pain Analysis */}
            {synthesis?.pain_analysis && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Activity className="w-4 h-4 text-red-400" />
                    Pain Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-3">
                    <TrendIndicator trend={synthesis.pain_analysis.trend} label={`Pain ${synthesis.pain_analysis.trend}`} />
                    {synthesis.pain_analysis.current_score !== null && (
                      <span className="text-lg font-bold">{synthesis.pain_analysis.current_score}/10</span>
                    )}
                  </div>
                  {synthesis.pain_analysis.alerts?.length > 0 && (
                    <div className="space-y-1">
                      {synthesis.pain_analysis.alerts.slice(0, 2).map((alert, idx) => (
                        <div key={idx} className="text-xs p-2 bg-red-500/10 rounded">
                          <AlertTriangle className="w-3 h-3 inline mr-1 text-red-400" />
                          {alert.message}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* PPS Analysis */}
            {synthesis?.pps_analysis && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-amber-400" />
                    Functional Status (PPS)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-3">
                    <TrendIndicator trend={synthesis.pps_analysis.trend} />
                    <PPSPhaseBadge 
                      phase={synthesis.pps_analysis.phase} 
                      score={synthesis.pps_analysis.current} 
                    />
                  </div>
                  {synthesis.pps_analysis.decline > 0 && (
                    <div className="text-sm">
                      <span className="text-slate-400">14-Day Change:</span>
                      <span className="text-red-400 ml-2">
                        <ArrowDown className="w-3 h-3 inline" />
                        {synthesis.pps_analysis.decline}%
                      </span>
                      <span className="text-slate-500 ml-2">
                        ({synthesis.pps_analysis.previous}% → {synthesis.pps_analysis.current}%)
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Compliance Status */}
          {compliance && <ComplianceStatusCard compliance={compliance} />}

          {/* Symptom Analysis */}
          {synthesis?.symptom_analysis?.length > 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Symptom Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {synthesis.symptom_analysis.map((symptom, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-900 rounded">
                      <span className="text-sm capitalize">{symptom.symptom}</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs capitalize">
                          {symptom.current_severity}
                        </Badge>
                        <TrendIndicator trend={symptom.trend} />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* POC Changes Tab */}
        <TabsContent value="changes" className="space-y-3">
          {proposedChanges.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-8 text-center">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
                <p className="text-slate-300">No pending POC changes</p>
                <p className="text-xs text-slate-500 mt-1">Run synthesis to analyze recent documentation</p>
              </CardContent>
            </Card>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-3">
                {proposedChanges.map((change) => (
                  <ProposedChangeCard 
                    key={change.id} 
                    change={change} 
                    onReview={handleReviewChange}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-3">
          {frequencyAlerts.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-8 text-center">
                <Bell className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-300">No active frequency alerts</p>
              </CardContent>
            </Card>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-3">
                {frequencyAlerts.map((alert) => (
                  <FrequencyAlertCard 
                    key={alert.id} 
                    alert={alert} 
                    onAcknowledge={handleAcknowledgeAlert}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </TabsContent>

        {/* IDG Items Tab */}
        <TabsContent value="idg" className="space-y-3">
          {(!dashboard?.idg_items || dashboard.idg_items.length === 0) ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-8 text-center">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-300">No items flagged for IDG</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-400" />
                  IDG Discussion Items
                </CardTitle>
                <CardDescription className="text-xs">
                  Items requiring interdisciplinary team review
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboard.idg_items.map((item, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded border ${
                        item.startsWith("[URGENT]") || item.startsWith("[PRIORITY]")
                          ? "bg-red-500/10 border-red-500/30"
                          : item.startsWith("COMPLIANCE")
                          ? "bg-amber-500/10 border-amber-500/30"
                          : "bg-slate-900 border-slate-700"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <ChevronRight className="w-4 h-4 mt-0.5 text-slate-400" />
                        <span className="text-sm">{item}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-700">
        <Button variant="ghost" onClick={onClose}>Close</Button>
        <p className="text-xs text-slate-500">
          Last synthesis: {synthesis?.synthesis_date 
            ? new Date(synthesis.synthesis_date).toLocaleString() 
            : "Never"}
        </p>
      </div>

      {/* Review Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => !open && setReviewDialog({ open: false, changeId: null, action: null })}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle>
              {reviewDialog.action === "approve" ? "Approve" : "Reject"} POC Change
            </DialogTitle>
            <DialogDescription>
              {reviewDialog.action === "approve" 
                ? "This change will be applied to the active Plan of Care."
                : "This change will be marked as rejected and not applied."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400">Review Notes (Optional)</label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add any notes about this decision..."
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewDialog({ open: false, changeId: null, action: null })}>
              Cancel
            </Button>
            <Button 
              onClick={submitReview}
              className={reviewDialog.action === "approve" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
            >
              {reviewDialog.action === "approve" ? "Approve & Sign" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ClinicalLogicDashboard;
